import React, { useState, useRef, useEffect } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { getProductDisplayInfo } from '../utils/productUtils';
import './FeaturedProductsCarousel.css';

const FeaturedProductsCarousel = ({ products = [], limit = 6 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const carouselRef = useRef(null);
  const navigate = useNavigate();

  // Limit products to featured ones
  const featuredProducts = products.slice(0, limit);

  // Mouse position tracking for 3D tilt
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { damping: 25, stiffness: 200 };
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [7.5, -7.5]), springConfig);
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-7.5, 7.5]), springConfig);

  const handleMouseMove = (e) => {
    if (!carouselRef.current || !isHovered) return;

    const rect = carouselRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;

    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    setIsHovered(false);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const nextSlide = () => {
    setActiveIndex((prev) => (prev + 1) % featuredProducts.length);
    x.set(0);
    y.set(0);
  };

  const prevSlide = () => {
    setActiveIndex((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);
    x.set(0);
    y.set(0);
  };

  const handleSlideClick = (product) => {
    navigate(`/product/${product.slug}`);
  };

  // Touch swipe handlers
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }
  };

  // Auto-reset mouse position on slide change
  useEffect(() => {
    x.set(0);
    y.set(0);
  }, [activeIndex, x, y]);

  if (featuredProducts.length === 0) return null;

  return (
    <div className="aceternity-carousel-container">
      <div className="aceternity-carousel-header">
        <h2 className="aceternity-carousel-title">Featured Products</h2>
        <p className="aceternity-carousel-subtitle">Handpicked favorites from Handmade Haven</p>
      </div>

      <div
        className="aceternity-carousel-wrapper"
        ref={carouselRef}
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="aceternity-carousel-track">
          {featuredProducts.map((product, index) => {
            const isActive = index === activeIndex;
            const offset = index - activeIndex;
            const absOffset = Math.abs(offset);
            const direction = offset > 0 ? 1 : -1;

            // Calculate position and scale
            let translateX = offset * 100;
            let scale = 0.85;
            let opacity = 0.4;
            let zIndex = featuredProducts.length - absOffset;

            if (isActive) {
              translateX = 0;
              scale = 1;
              opacity = 1;
              zIndex = featuredProducts.length + 1;
            }

            // Determine display price and image using utility
            const { displayPrice, displayImage } = getProductDisplayInfo(product);

            return (
              <motion.div
                key={product.id}
                className={`aceternity-carousel-slide ${isActive ? 'active' : ''}`}
                style={{
                  x: translateX,
                  scale,
                  opacity,
                  zIndex,
                  rotateX: isActive && isHovered ? rotateX : 0,
                  rotateY: isActive && isHovered ? rotateY : 0,
                  transformStyle: 'preserve-3d',
                }}
                onClick={() => !isActive && handleSlideClick(product)}
                animate={{
                  x: translateX,
                  scale,
                  opacity,
                }}
                transition={{
                  type: 'spring',
                  stiffness: 300,
                  damping: 30,
                }}
              >
                <div className="aceternity-slide-content">
                  <div className="aceternity-slide-image-wrapper">
                    <img
                      src={displayImage}
                      alt={product.name}
                      className="aceternity-slide-image"
                      loading="lazy"
                      decoding="async"
                    />
                    {isActive && (
                      <div className="aceternity-slide-overlay">
                        <div className="aceternity-slide-info">
                          <h3 className="aceternity-slide-name">{product.name}</h3>
                          <div className="aceternity-slide-price">₹ {displayPrice}</div>
                          <button
                            className="aceternity-slide-cta"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSlideClick(product);
                            }}
                          >
                            View Product
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Pagination Dots - Mobile Only */}
        <div className="aceternity-carousel-pagination">
          {featuredProducts.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`aceternity-carousel-dot ${index === activeIndex ? 'active' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveIndex(index);
              }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Desktop Arrows - Below Carousel */}
      <div className="aceternity-carousel-arrows">
        <button
          type="button"
          className="aceternity-carousel-arrow aceternity-carousel-arrow-prev"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            prevSlide();
          }}
          aria-label="Previous slide"
        >
          <IconChevronLeft size={28} strokeWidth={2} />
        </button>
        <button
          type="button"
          className="aceternity-carousel-arrow aceternity-carousel-arrow-next"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            nextSlide();
          }}
          aria-label="Next slide"
        >
          <IconChevronRight size={28} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default FeaturedProductsCarousel;

